import { request, ApiError } from './core';

export interface SocialLink {
  url: string;
  label: string;
  platform: string;
}

export interface Portfolio {
  ID: string;
  UserID: string;
  Slug: string;
  DisplayName: string;
  Tagline: string;
  Headline: string;
  Bio: string;
  Location: string;
  Instagram: string;
  Website: string;
  ContactEmail: string;
  CoverAssetID: string;
  AvatarAssetID: string;
  LogoAssetID: string;
  TemplateID: string;
  IsPublished: boolean;
  SocialLinks: SocialLink[];
  AccentColor: string;
  BackgroundColor: string;
  TextColor: string;
  DarkPrimary: string;
  DarkBackground: string;
  DarkText: string;
  FontFamily: string;
  ClientPortalEnabled: boolean;
  ClientPortalWelcome: string;
  CreatedAt: string;
  UpdatedAt: string;
  // Presigned URLs returned by GET /api/v1/portfolios/me
  LogoURL?: string;
  AvatarURL?: string;
  CoverURL?: string;
}

export interface PortfolioTemplate {
  ID: string;
  Name: string;
}

export interface PortfolioUpdate {
  display_name: string;
  tagline?: string;
  headline?: string;
  bio?: string;
  location?: string;
  instagram?: string;
  contact_email?: string;
  cover_asset_id?: string;
  avatar_asset_id?: string;
  logo_asset_id?: string;
  template_id?: string;
  accent_color?: string;
  background_color?: string;
  text_color?: string;
  dark_primary?: string;
  dark_background?: string;
  dark_text?: string;
  font_family?: string;
  is_published: boolean;
  social_links?: Array<{ url: string; label?: string }>;
  client_portal_enabled?: boolean;
  client_portal_welcome?: string;
}

export const portfolioApi = {
  getPortfolio: (slug: string) =>
    request<Portfolio>("GET", `/api/v1/portfolios/${encodeURIComponent(slug)}`),
  getMyPortfolio: () => request<Portfolio>("GET", "/api/v1/portfolios/me"),
  updateMyPortfolio: (p: PortfolioUpdate) =>
    request<Portfolio>("PUT", "/api/v1/portfolios/me", p),
  listPortfolioTemplates: () =>
    request<PortfolioTemplate[]>("GET", "/api/v1/portfolios/templates"),
  createPortfolio: (p: {
    slug: string;
    display_name: string;
    headline?: string;
    bio?: string;
    contact_email?: string;
    accent_color?: string;
  }) => request<Portfolio>("POST", "/api/v1/portfolios", p),

  uploadPortfolioAsset: async (file: File, type?: "logo" | "avatar" | "cover"): Promise<{ key: string; url: string }> => {
    const form = new FormData();
    form.append("file", file);
    const qs = type ? `?type=${type}` : "";
    const res = await fetch(`/api/v1/portfolios/me/assets${qs}`, {
      method: "POST",
      body: form,
      credentials: "same-origin",
    });
    if (!res.ok) {
      const text = await res.text();
      let message = res.statusText;
      try {
        const parsed = JSON.parse(text) as { error?: string };
        if (parsed.error) message = parsed.error;
      } catch { /* ignore */ }
      throw new ApiError(res.status, message);
    }
    return (await res.json()) as { key: string; url: string };
  },
};
